import { Box, Stack, Tooltip, Typography } from "@mui/material";
import {
  Confidences,
  ScrapEyesEvent,
} from "components/scrapEyeLabelling/types";
import {
  FLAG_OUTLINE_COLOR,
  getHighestPriorityReasonConfig,
} from "components/manualReview/utils/reviewReasons";
import dayjs from "dayjs";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMaterialColor, useMaterials } from "hooks/useMaterials";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { ReviewReason } from "src/store/api/generatedApi";
import { eventTypeColor } from "../utils";
import { AreaLabel } from "components/scrapEyeLabelling/components/AreaLabel";
import {
  getLabellingMaterialDisplayName,
  getRawMaterialDisplayName,
} from "components/scrapEyeLabelling/utils/materialDisplayName";
import { useFormatNumber } from "src/utils/formatNumber";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { resolveEventMass } from "components/manualReview/utils/massEventWindow";
import { ConfidenceThresholds } from "src/store/api/generatedApi";

type EventWithReviewData = ScrapEyesEvent & {
  review_reasons?: ReviewReason[];
  resolved?: boolean;
};

const getEventVisualStatus = (
  event: EventWithReviewData,
  selectedEventId?: string | null
): { isSelected: boolean; themeColor: string | null } => {
  if (event.id === selectedEventId)
    return { isSelected: true, themeColor: null };

  const config = getHighestPriorityReasonConfig(event.review_reasons);
  if (!config) {
    if (event.comment) {
      return { isSelected: false, themeColor: FLAG_OUTLINE_COLOR };
    }
    return { isSelected: false, themeColor: null };
  }

  if (config.isConflict && event.resolved)
    return { isSelected: false, themeColor: "success.main" };
  return { isSelected: false, themeColor: FLAG_OUTLINE_COLOR };
};

const outlineRing = (width: number) => ({
  ml: `-${width}px`,
  mt: `-${width}px`,
  borderWidth: width,
  borderStyle: "solid",
});

const MISMATCH_RING_WIDTH = 2;
const FLAG_RING_WIDTH = 3;
const OUTLINE_CLEARANCE = Math.max(FLAG_RING_WIDTH, MISMATCH_RING_WIDTH * 2);

export const DEFAULT_OVERBAR_HEIGHT = 10;

export const overbarOffset = (overbarHeight: number): number =>
  overbarHeight + OUTLINE_CLEARANCE;

const outlineStyles = (
  isSelected: boolean,
  eventColor: string,
  hasMismatchWarning: boolean,
  isActive: boolean,
  themeColor: string | null,
  highlightColor: string | null | undefined
) => {
  if (hasMismatchWarning)
    return {
      borderColor: "error.main",
      ...outlineRing(MISMATCH_RING_WIDTH),
      boxShadow: highlightColor
        ? `0 0 0 ${MISMATCH_RING_WIDTH}px ${highlightColor}, 0 0 6px rgba(211, 47, 47, 0.6)`
        : "0 0 6px rgba(211, 47, 47, 0.6)",
    };
  if (highlightColor)
    return {
      borderColor: highlightColor,
      ...outlineRing(FLAG_RING_WIDTH),
      ...(isSelected ? { boxShadow: `0 0 8px ${eventColor}` } : {}),
    };
  if (isSelected)
    return {
      borderColor: "primary.main",
      ...outlineRing(0.1),
      boxShadow: `0 0 8px ${eventColor}`,
    };
  const outlineColor = highlightColor === null ? null : themeColor;
  if (outlineColor)
    return {
      borderColor: outlineColor,
      ...outlineRing(2),
      boxShadow: `0 0 4px ${outlineColor}`,
    };
  if (isActive)
    return {
      borderColor: eventColor,
      ...outlineRing(1),
      boxShadow: `0 0 6px ${eventColor}`,
    };
  return {};
};

const hasLowConfidence = (
  confidences: Confidences | undefined,
  thresholds: ConfidenceThresholds
): boolean => {
  if (!confidences) return false;
  return Object.entries(confidences).some(([key, value]) => {
    if (value === undefined) return false;
    const threshold = thresholds[key as keyof ConfidenceThresholds];
    return threshold !== undefined && value < threshold;
  });
};

const MIN_WIDTH_PERCENT = 0.1;

export const VideoTimelineEvent = React.memo(
  ({
    event,
    index,
    selectedEventId,
    onEventClick,
    highlightColor,
    colorEventsBy = "type",
    materialNameSource = "labelling",
    allEvents,
    adjacentPreview,
    videoStart,
    duration,
    overbarHeight = DEFAULT_OVERBAR_HEIGHT,
    minWidthPercentage = MIN_WIDTH_PERCENT,
    isActive = false,
    isModified = false,
  }: {
    event: ScrapEyesEvent;
    index?: number;
    selectedEventId?: string | null;
    onEventClick?: (eventId: string) => void;
    highlightColor?: string | null;
    videoStart: dayjs.Dayjs;
    colorEventsBy?: "type" | "material";
    materialNameSource?: "labelling" | "raw";
    allEvents?: ScrapEyesEvent[];
    adjacentPreview?: ReactNode;
    duration: number;
    overbarHeight?: number;
    minWidthPercentage?: number;
    isActive?: boolean;
    isModified?: boolean;
  }) => {
    const { getColor } = useMaterialColor();
    const { t } = useTenantTranslation();
    const { materials } = useMaterials();
    const formatNumber = useFormatNumber()(2, false);
    const formatUnits = useUnitsFormatter(false);
    const metadata = useScrapEyeMetadata();
    const confidenceThresholds =
      metadata.label_review_config.warnings.confidence_thresholds;

    const [pulseDelay, setPulseDelay] = useState<string | null>(null);
    useEffect(() => {
      if (isModified) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect -- initializes animation offset only once per modification
        setPulseDelay((prev) => prev ?? `-${Date.now() % 2000}ms`);
      } else {
        // eslint-disable-next-line @eslint-react/set-state-in-effect -- clears animation delay when no longer modified
        setPulseDelay(null);
      }
    }, [isModified]);

    const startSeconds = useMemo(
      () => event.start.diff(videoStart, "second"),
      [event.start, videoStart]
    );

    const endSeconds = useMemo(
      () => event.end.diff(videoStart, "second"),
      [event.end, videoStart]
    );

    const startPercent = useMemo(
      () => (startSeconds / duration) * 100,
      [startSeconds, duration]
    );

    const widthPercent = useMemo(
      () =>
        Math.max(
          ((endSeconds - startSeconds) / duration) * 100,
          minWidthPercentage
        ),
      // eslint-disable-next-line @eslint-react/exhaustive-deps -- minWidthPercentage is a constant prop that does not change; omitting avoids unnecessary recalculation
      [endSeconds, startSeconds, duration]
    );

    const color = useMemo(() => {
      if (event.color !== undefined) return event.color;
      return colorEventsBy === "type"
        ? eventTypeColor(event.eventType)
        : getColor(event.material ?? "", event.eventType === "flag");
    }, [event.color, event.eventType, colorEventsBy, getColor, event.material]);

    const { isSelected, themeColor } = useMemo(
      () => getEventVisualStatus(event as EventWithReviewData, selectedEventId),
      [event, selectedEventId]
    );

    const hasMismatchWarning = useMemo(
      () => event.warnings?.includes("materialMismatchWithActual") ?? false,
      [event.warnings]
    );

    const pairedEvent = useMemo(() => {
      if (!event.pairId || !allEvents) return null;
      return allEvents.find(
        (e) => e.pairId === event.pairId && e.id !== event.id
      );
    }, [event.pairId, event.id, allEvents]);

    const shouldConnectToPairedEvent = useMemo(() => {
      if (!pairedEvent || !allEvents) return false;
      const currentIndex = allEvents.findIndex((e) => e.id === event.id);
      const pairedIndex = allEvents.findIndex((e) => e.id === pairedEvent.id);
      return currentIndex < pairedIndex;
    }, [pairedEvent, event.id, allEvents]);

    const hasPairMaterialMismatch = useMemo(
      () =>
        pairedEvent != null &&
        event.material !== null &&
        pairedEvent.material !== null &&
        event.material !== pairedEvent.material,
      [pairedEvent, event.material]
    );

    // The lift draws the connector spanning to its drop, so the material-mismatch
    // ⚠ marker lives there. The cross-boundary 🔗 is its own per-event flag instead
    // (like pairPartnerBeyondWindow below): a crossing sequence isn't always exactly
    // two paired events, so it can't always be anchored to a single connector.
    const showMismatchSplit =
      shouldConnectToPairedEvent && hasPairMaterialMismatch;
    const showCrossBoundaryLink = event.pairCrossesReviewPeriod ?? false;
    // An unpaired end whose partner is beyond the window carries its own 🔗 on
    // the strip above the single bar (there is no connector to a partner).
    const showBeyondWindowLink = event.pairPartnerBeyondWindow ?? false;

    const pairedEventStartPercent = useMemo(() => {
      if (!pairedEvent) return 0;
      const pairedStartSeconds = pairedEvent.start.diff(videoStart, "second");
      return (pairedStartSeconds / duration) * 100;
    }, [pairedEvent, duration, videoStart]);

    const pairedEventWidthPercent = useMemo(() => {
      if (!pairedEvent) return 0;
      return Math.max(
        (pairedEvent.end.diff(pairedEvent.start, "second") / duration) * 100,
        minWidthPercentage
      );
      // eslint-disable-next-line @eslint-react/exhaustive-deps -- minWidthPercentage is a constant prop that does not change; omitting avoids unnecessary recalculation
    }, [pairedEvent, duration]);
    const connectorLeft = useMemo(() => startPercent, [startPercent]);

    const connectorWidth = useMemo(() => {
      if (shouldConnectToPairedEvent) {
        return pairedEventStartPercent - startPercent + pairedEventWidthPercent;
      }
      return widthPercent;
    }, [
      shouldConnectToPairedEvent,
      pairedEventStartPercent,
      startPercent,
      pairedEventWidthPercent,
      widthPercent,
    ]);

    const resolveMaterialDisplayName =
      materialNameSource === "raw"
        ? getRawMaterialDisplayName
        : getLabellingMaterialDisplayName;
    const materialDisplayName = resolveMaterialDisplayName(
      materials?.find((material) => material.name === event.material) ?? null,
      t
    ).toUpperCase();
    const coMaterialDisplayName = event.coMaterial
      ? resolveMaterialDisplayName(
          materials?.find((material) => material.name === event.coMaterial) ??
            null,
          t
        ).toUpperCase()
      : null;

    const massToDisplay = resolveEventMass({
      event,
      massEventMatching: metadata.mass_event_matching,
    });
    const formattedMass =
      massToDisplay === null
        ? undefined
        : `${formatNumber(massToDisplay)}${formatUnits("mass")}`;

    const tooltipContent = useMemo(
      () => (
        <Stack spacing={0.5} p={0.5}>
          {event.hoverLabel !== undefined && (
            <Typography variant="caption" fontWeight="bold">
              {event.hoverLabel}
            </Typography>
          )}
          {index !== undefined && (
            <Typography variant="caption" fontWeight="bold">
              {t("scrapEyesEventLabel", { index: index + 1 })}
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                backgroundColor: eventTypeColor(event.eventType),
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "white", fontWeight: "bold", fontSize: "0.65rem" }}
              >
                {event.eventType.toUpperCase()}
              </Typography>
            </Box>
            {event.eventType !== "flag" ? (
              <>
                <AreaLabel area={event.area} label={event.areaLabel} />
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    backgroundColor: getColor(event.material ?? ""),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.65rem",
                    }}
                  >
                    {materialDisplayName}
                  </Typography>
                </Box>
                {event.coMaterial ? (
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      backgroundColor: getColor(event.coMaterial),
                      backgroundImage: `repeating-linear-gradient(45deg, transparent 0 6px, ${getColor(
                        event.coMaterial
                      )}66 6px 12px)`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.65rem",
                      }}
                    >
                      + {coMaterialDisplayName}
                    </Typography>
                  </Box>
                ) : null}
                {event.warnings && event.warnings.length > 0 ? (
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      backgroundColor: "warning.main",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.65rem",
                      }}
                    >
                      ⚠
                    </Typography>
                  </Box>
                ) : null}
                {formattedMass ? (
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      backgroundColor: "grey.600",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.65rem",
                      }}
                    >
                      {formattedMass}
                    </Typography>
                  </Box>
                ) : null}
              </>
            ) : null}
          </Stack>
          <Typography variant="caption" fontSize="0.65rem">
            {event.start.utc().format("LTS")} - {event.end.utc().format("LTS")}
          </Typography>
          <Typography variant="caption" fontSize="0.65rem">
            {t("duration")}: {(endSeconds - startSeconds).toFixed(1)}
            {"s"}
          </Typography>
          {event.comment ? (
            <Typography
              variant="caption"
              fontSize="0.65rem"
              sx={{
                fontStyle: "italic",
                mt: 0.5,
                maxWidth: 200,
                wordWrap: "break-word",
              }}
            >
              {event.comment}
            </Typography>
          ) : null}
          {event.confidences ? (
            <Stack spacing={0} mt={0.5}>
              <Typography
                variant="caption"
                fontSize="0.65rem"
                fontWeight="bold"
              >
                {t("confidence")}:
              </Typography>
              {Object.entries(event.confidences).map(([label, value]) => {
                const threshold =
                  confidenceThresholds[label as keyof ConfidenceThresholds];
                return (
                  <Typography
                    key={label}
                    variant="caption"
                    fontSize="0.65rem"
                    sx={{
                      color:
                        threshold !== undefined && value < threshold
                          ? "warning.main"
                          : "white",
                    }}
                  >
                    {label}: {(value * 100).toFixed(0)}%
                  </Typography>
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      ),
      // eslint-disable-next-line @eslint-react/exhaustive-deps -- materialDisplayName, formattedMass, and formatNumber are derived from listed deps; including them would cause extra re-renders without visual change
      [
        event.eventType,
        event.hoverLabel,
        event.material,
        event.coMaterial,
        event.mass,
        event.recordedMass,
        metadata.mass_event_matching,
        event.warnings,
        event.comment,
        event.confidences,
        event.start,
        event.end,
        index,
        startSeconds,
        endSeconds,
        getColor,
        t,
      ]
    );

    const adjacentPreviewDivider = adjacentPreview ? (
      <Box
        sx={{
          mt: 0.5,
          pt: 0.5,
          borderTop: "1px solid",
          borderColor: "grey.600",
        }}
      >
        {adjacentPreview}
      </Box>
    ) : null;
    const adjacentPreviewSlotProps = adjacentPreview
      ? { tooltip: { sx: { maxWidth: "none" } } }
      : undefined;

    return (
      <>
        <Tooltip
          title={
            <>
              {tooltipContent}
              {adjacentPreviewDivider}
            </>
          }
          slotProps={adjacentPreviewSlotProps}
          arrow
          placement="top"
        >
          <Box
            key={event.id}
            data-testid="timeline-event"
            onClick={() => onEventClick?.(event.id)}
            sx={{
              position: "absolute",
              left: `${startPercent}%`,
              width: `${widthPercent}%`,
              minWidth: `3px`,
              height: "100%",
              cursor: onEventClick ? "pointer" : "default",
              pointerEvents: onEventClick ? "auto" : undefined,
              opacity: event.opacity,
              zIndex:
                hasMismatchWarning || themeColor !== null
                  ? 10
                  : isActive
                    ? 3
                    : 1,
              "&:hover": {
                opacity: 1,
                transform: onEventClick ? "scaleY(1.2)" : "none",
                zIndex: 3,
              },
              transition: "opacity 0.2s, transform 0.2s",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: color,
                borderRadius: 0.5,
                opacity: isSelected || isActive ? 1 : 0.8,
                ...outlineStyles(
                  isSelected,
                  color,
                  hasMismatchWarning,
                  isActive,
                  themeColor,
                  highlightColor
                ),
                ...(isModified &&
                  !isSelected &&
                  !hasMismatchWarning && {
                    "@keyframes pulseModified": {
                      "0%, 100%": {
                        boxShadow: "0 0 3px 1px rgba(33, 150, 243, 0.4)",
                      },
                      "50%": {
                        boxShadow: "0 0 6px 2px rgba(33, 150, 243, 0.7)",
                      },
                    },
                    animation: "pulseModified 2s ease-in-out infinite",
                    animationDelay: pulseDelay ?? undefined,
                    ...(!themeColor && {
                      borderColor: "info.main",
                      borderWidth: 1,
                      borderStyle: "solid",
                    }),
                  }),
                position: "relative",
              }}
            >
              {event.comment ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: -3,
                    right: -3,
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "error.main",
                    border: "1.5px solid #333",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    zIndex: 1,
                  }}
                />
              ) : null}
              {!event.comment &&
              hasLowConfidence(event.confidences, confidenceThresholds) ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: -3,
                    right: -3,
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "data.red",
                    border: "1.5px solid #333",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    zIndex: 1,
                  }}
                />
              ) : null}
            </Box>
          </Box>
        </Tooltip>
        <Box
          data-testid="timeline-event-overbar"
          sx={{
            position: "absolute",
            left: `${connectorLeft}%`,
            width: `${connectorWidth}%`,
            minWidth: `5px`,
            top: `-${overbarOffset(overbarHeight)}px`,
            height: `${overbarHeight}px`,
            display: "flex",
            overflow: "hidden",
            pointerEvents: "none",
            opacity: event.opacity,
            ...((showCrossBoundaryLink || showBeyondWindowLink) && {
              border: "1px dashed",
              borderColor: "text.secondary",
            }),
            ...(showMismatchSplit
              ? {}
              : {
                  backgroundColor: getColor(
                    shouldConnectToPairedEvent
                      ? (pairedEvent?.material ?? "")
                      : (event.material ?? "")
                  ),
                }),
          }}
        >
          {showMismatchSplit ? (
            <>
              <Box
                sx={{
                  width: "50%",
                  height: "100%",
                  backgroundColor: getColor(event.material ?? ""),
                }}
              />
              <Box
                sx={{
                  width: "50%",
                  height: "100%",
                  backgroundColor: getColor(pairedEvent?.material ?? ""),
                }}
              />
            </>
          ) : null}
          {showMismatchSplit ||
          showCrossBoundaryLink ||
          showBeyondWindowLink ? (
            <Tooltip
              title={
                <>
                  <Typography variant="caption">
                    {[
                      showBeyondWindowLink && t("pairPartnerBeyondWindow"),
                      showCrossBoundaryLink && t("pairCrossesReviewPeriod"),
                      showMismatchSplit && t("materialMismatchWithPair"),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                  {adjacentPreviewDivider}
                </>
              }
              slotProps={adjacentPreviewSlotProps}
              arrow
            >
              <Stack
                direction="row"
                spacing={0.25}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  alignItems: "center",
                  pointerEvents: "auto",
                  cursor: "help",
                }}
              >
                {showCrossBoundaryLink || showBeyondWindowLink ? (
                  <Typography
                    sx={{
                      fontSize: `${Math.max(overbarHeight - 1, 8)}px`,
                      lineHeight: 1,
                    }}
                  >
                    🔗
                  </Typography>
                ) : null}
                {showMismatchSplit ? (
                  <Typography
                    sx={{
                      fontSize: `${Math.max(overbarHeight - 1, 8)}px`,
                      lineHeight: 1,
                      color: "error.main",
                    }}
                  >
                    ⚠
                  </Typography>
                ) : null}
              </Stack>
            </Tooltip>
          ) : null}
        </Box>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    const prevWarnings = prevProps.event.warnings ?? [];
    const nextWarnings = nextProps.event.warnings ?? [];
    const warningsEqual =
      prevWarnings.length === nextWarnings.length &&
      prevWarnings.every((w, i) => w === nextWarnings[i]);

    return (
      prevProps.event.id === nextProps.event.id &&
      prevProps.event.start.isSame(nextProps.event.start) &&
      prevProps.event.end.isSame(nextProps.event.end) &&
      prevProps.event.eventType === nextProps.event.eventType &&
      prevProps.event.hoverLabel === nextProps.event.hoverLabel &&
      prevProps.event.material === nextProps.event.material &&
      prevProps.event.coMaterial === nextProps.event.coMaterial &&
      prevProps.event.mass === nextProps.event.mass &&
      prevProps.event.recordedMass === nextProps.event.recordedMass &&
      warningsEqual &&
      prevProps.event.shortLabel === nextProps.event.shortLabel &&
      prevProps.event.comment === nextProps.event.comment &&
      prevProps.event.confidences === nextProps.event.confidences &&
      prevProps.event.pairId === nextProps.event.pairId &&
      prevProps.event.pairCrossesReviewPeriod ===
        nextProps.event.pairCrossesReviewPeriod &&
      prevProps.event.pairPartnerBeyondWindow ===
        nextProps.event.pairPartnerBeyondWindow &&
      prevProps.event.area === nextProps.event.area &&
      prevProps.materialNameSource === nextProps.materialNameSource &&
      prevProps.selectedEventId === nextProps.selectedEventId &&
      prevProps.onEventClick === nextProps.onEventClick &&
      prevProps.highlightColor === nextProps.highlightColor &&
      prevProps.allEvents === nextProps.allEvents &&
      prevProps.adjacentPreview === nextProps.adjacentPreview &&
      prevProps.videoStart.isSame(nextProps.videoStart) &&
      prevProps.duration === nextProps.duration &&
      prevProps.isActive === nextProps.isActive &&
      prevProps.isModified === nextProps.isModified
    );
  }
);
VideoTimelineEvent.displayName = "VideoTimelineEvent";
